import { STATUS } from "@/constants/statuses";
import { localizationInfoProviderToken } from "@/tokens";
import { hasNextPage } from "@/utils/paginationUtils/paginationUtils";
import { REWARDS_STORE_UPDATE_STRATEGY } from "@apps/rewards/constants/storeUpdateStrategy";
import RewardsHistoryStore from "@apps/rewards/modules/History/store/RewardsHistoryStore";
import type { REWARDS_STORE_UPDATE_STRATEGY_TYPE } from "@apps/rewards/types";
import { getLoadingStatus } from "@apps/rewards/utils/statuses";
import { applyTransaction } from "@datorama/akita";
import type { LocalizationInfoProviderInterface } from "@lib/LocalizationInfoProvider/LocalizationInfoProviderInterface";
import type { Observable } from "rxjs";
import { catchError, map, tap } from "rxjs";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import { rewardsApplicationLoggerToken } from "../../../tokens";
import RewardsHistoryQuery from "../query/RewardsHistoryQuery";
import RewardsHistoryApiService from "../RewardsHistoryApiService";
import type {
    RewardsHistoryEntity,
    RewardsHistoryResponseType,
} from "../RewardsHistoryEntities";
import type {
    RewardsGroupedHistoryList,
    RewardsHistoryQueryParams,
} from "../types";

@singleton()
class RewardsHistoryService {
    constructor(
        private readonly rewardsHistoryApiService: RewardsHistoryApiService,
        private readonly rewardsHistoryStore: RewardsHistoryStore,
        private readonly rewardsHistoryQuery: RewardsHistoryQuery,
        @inject(localizationInfoProviderToken)
        private readonly localizationInfoProvider: LocalizationInfoProviderInterface,
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {}

    public selectOffset(): Observable<number> {
        return this.rewardsHistoryQuery.paginationMeta$.pipe(
            map(
                (paginationMeta) =>
                    paginationMeta.perPage + paginationMeta.currentOffset
            )
        );
    }

    public hasNextPage(): Observable<boolean> {
        return this.rewardsHistoryQuery.paginationMeta$.pipe(
            map((paginationMeta) => hasNextPage(paginationMeta))
        );
    }

    private groupByMonth(
        historyList: RewardsHistoryEntity[]
    ): RewardsGroupedHistoryList {
        return historyList.reduce((acc: RewardsGroupedHistoryList, item) => {
            const month = new Date(item.createdAt).toLocaleString(
                this.localizationInfoProvider.getGwI18nLocale(),
                {
                    month: "long",
                    year: "numeric",
                }
            );

            const existingGroup = acc.find((group) => group.month === month);

            if (existingGroup) {
                existingGroup.historyItems.push(item);
            } else {
                acc.push({
                    month,
                    historyItems: [item],
                });
            }

            return acc;
        }, []);
    }

    public selectGroupedHistoryList(
        items: Observable<RewardsHistoryEntity[]>
    ): Observable<RewardsGroupedHistoryList> {
        return items.pipe(map((historyList) => this.groupByMonth(historyList)));
    }

    getHistory(
        queryParams: RewardsHistoryQueryParams,
        storeUpdateStrategy: REWARDS_STORE_UPDATE_STRATEGY_TYPE
    ): Observable<RewardsHistoryResponseType> {
        const status = this.rewardsHistoryStore.getValue().status;
        this.rewardsHistoryStore.setStatus(getLoadingStatus(status));

        return this.rewardsHistoryApiService
            .getActivityHistory(queryParams)
            .pipe(
                catchError((error: unknown) => {
                    this.logger.debug(
                        "RewardsHistoryApiService.getHistory is errored",
                        error
                    );
                    this.rewardsHistoryStore.setStatus(STATUS.ERROR);
                    throw error;
                }),
                tap((value) => {
                    this.logger.debug(
                        "RewardsHistoryApiService.getHistory is ready"
                    );
                    //Store Update
                    applyTransaction(() => {
                        if (
                            storeUpdateStrategy ===
                            REWARDS_STORE_UPDATE_STRATEGY.add
                        ) {
                            this.rewardsHistoryStore.add(value.items);
                        } else {
                            this.rewardsHistoryStore.set(value.items);
                        }

                        this.rewardsHistoryStore.update(() => ({
                            paginationMeta: {
                                currentOffset: value.currentOffset,
                                perPage: value.perPage,
                                totalCount: value.totalCount,
                            },
                        }));
                        this.rewardsHistoryStore.setStatus(STATUS.READY);
                    });
                })
            );
    }
}

export default RewardsHistoryService;
